'use client';
import React, { useState } from 'react';
import { AlertTriangle, Shield, Eye, Ban, Clock, CheckCircle, XCircle, RefreshCw, Filter } from 'lucide-react';

interface FraudAlert {
  id: string;
  type: string;
  user: string;
  userId: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  score: number;
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  time: string;
  ip: string;
  country: string;
}

const alerts: FraudAlert[] = [
  { id: 'FRD-001', type: 'Multi-Account', user: 'James Wilson', userId: 'USR-15892', description: 'Multiple accounts detected from same device fingerprint. 3 accounts share identical device ID.', severity: 'critical', score: 95, status: 'open', time: '5m ago', ip: '192.168.1.45', country: 'UK' },
  { id: 'FRD-002', type: 'Referral Farming', user: 'Carlos Mendez', userId: 'USR-22891', description: 'Suspicious referral pattern — 47 referrals in 24h, all from same IP subnet.', severity: 'high', score: 82, status: 'investigating', time: '1h ago', ip: '10.0.0.123', country: 'Spain' },
  { id: 'FRD-003', type: 'Unusual Withdrawal', user: 'Unknown User', userId: 'USR-98012', description: 'Withdrawal request of $4,500 immediately after large deposit. No prior activity.', severity: 'high', score: 78, status: 'open', time: '2h ago', ip: '172.16.0.88', country: 'USA' },
  { id: 'FRD-004', type: 'Bot Detection', user: 'TestAccount99', userId: 'USR-44201', description: 'Inhuman response times detected in quiz — avg 0.3s per question over 200 questions.', severity: 'critical', score: 98, status: 'open', time: '3h ago', ip: '45.33.32.156', country: 'Unknown' },
  { id: 'FRD-005', type: 'VPN/Proxy', user: 'Anon User', userId: 'USR-77341', description: 'User accessing from known VPN exit node in restricted jurisdiction.', severity: 'medium', score: 61, status: 'investigating', time: '5h ago', ip: '104.21.45.67', country: 'Germany' },
  { id: 'FRD-006', type: 'Chargeback Abuse', user: 'Mike Johnson', userId: 'USR-33201', description: '3rd chargeback in 30 days. Total disputed amount: $340.', severity: 'high', score: 74, status: 'resolved', time: '1d ago', ip: '98.139.180.149', country: 'USA' },
  { id: 'FRD-007', type: 'Collusion', user: 'Group: Alpha', userId: 'GRP-001', description: 'Two users consistently losing to same opponent in paid matches. Pattern suggests collusion.', severity: 'medium', score: 55, status: 'dismissed', time: '2d ago', ip: 'Multiple', country: 'India' },
];

const severityConfig = {
  critical: { color: '#F43F5E', bg: 'rgba(244,63,94,0.12)', label: 'Critical' },
  high: { color: '#F97316', bg: 'rgba(249,115,22,0.12)', label: 'High' },
  medium: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: 'Medium' },
  low: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', label: 'Low' },
};

const statusConfig = {
  open: { color: '#F43F5E', icon: AlertTriangle, label: 'Open' },
  investigating: { color: '#F59E0B', icon: Clock, label: 'Investigating' },
  resolved: { color: '#10B981', icon: CheckCircle, label: 'Resolved' },
  dismissed: { color: '#64748B', icon: XCircle, label: 'Dismissed' },
};

export default function AdminFraudContent() {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<FraudAlert | null>(null);

  const filtered = alerts.filter(a => {
    const ms = severityFilter === 'all' || a.severity === severityFilter;
    const mst = statusFilter === 'all' || a.status === statusFilter;
    return ms && mst;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Fraud Alerts Center</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Monitor, investigate and resolve suspicious activity</p>
        </div>
        <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Open Alerts', value: '23', color: '#F43F5E', bg: 'rgba(244,63,94,0.12)' },
          { label: 'Investigating', value: '8', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
          { label: 'Resolved Today', value: '14', color: '#10B981', bg: 'rgba(16,185,129,0.12)' },
          { label: 'Avg Fraud Score', value: '34.2', color: '#A78BFA', bg: 'rgba(167,139,250,0.12)' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: s.bg }}>
              <Shield size={16} style={{ color: s.color }} />
            </div>
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Filter by:</span>
        {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => (
          <button key={s} onClick={() => setSeverityFilter(s)}
            className="text-xs px-3 py-1.5 rounded-full font-semibold transition-all capitalize"
            style={{
              background: severityFilter === s ? (s === 'all' ? '#7C3AED' : severityConfig[s]?.color || '#7C3AED') : 'var(--muted)',
              color: severityFilter === s ? '#fff' : 'var(--muted-foreground)',
            }}>
            {s === 'all' ? 'All Severity' : s}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {(['all', 'open', 'investigating', 'resolved', 'dismissed'] as const).map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className="text-xs px-3 py-1.5 rounded-full font-semibold transition-all capitalize"
              style={{
                background: statusFilter === s ? 'rgba(124,58,237,0.2)' : 'var(--muted)',
                color: statusFilter === s ? '#A78BFA' : 'var(--muted-foreground)',
                border: statusFilter === s ? '1px solid rgba(124,58,237,0.4)' : '1px solid transparent',
              }}>
              {s === 'all' ? 'All Status' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Alert List */}
      <div className="space-y-3">
        {filtered.map((alert) => {
          const sc = severityConfig[alert.severity];
          const stc = statusConfig[alert.status];
          return (
            <div key={alert.id} className="rounded-xl p-4 card-hover cursor-pointer"
              style={{ background: 'var(--card)', border: `1px solid ${selected?.id === alert.id ? sc.color + '60' : 'var(--border)'}` }}
              onClick={() => setSelected(selected?.id === alert.id ? null : alert)}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: sc.bg }}>
                  <AlertTriangle size={18} style={{ color: sc.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono" style={{ color: '#A78BFA' }}>{alert.id}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                          style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                        <span className="text-xs font-semibold text-foreground">{alert.type}</span>
                      </div>
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{alert.description}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>User: <span className="text-foreground">{alert.user}</span></span>
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>IP: {alert.ip}</span>
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Country: {alert.country}</span>
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{alert.time}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                          <div className="h-full rounded-full" style={{ width: `${alert.score}%`, background: alert.score > 80 ? '#F43F5E' : alert.score > 60 ? '#F59E0B' : '#10B981' }} />
                        </div>
                        <span className="text-xs font-bold" style={{ color: alert.score > 80 ? '#F43F5E' : alert.score > 60 ? '#F59E0B' : '#10B981' }}>{alert.score}</span>
                      </div>
                      <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: stc.color }}>
                        <stc.icon size={11} />{stc.label}
                      </span>
                    </div>
                  </div>

                  {selected?.id === alert.id && (
                    <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: 'var(--border)' }}>
                      <button className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5">
                        <Eye size={12} /> Investigate
                      </button>
                      <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                        style={{ background: 'rgba(244,63,94,0.15)', color: '#F43F5E' }}>
                        <Ban size={12} /> Ban User
                      </button>
                      <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                        style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                        <CheckCircle size={12} /> Mark Resolved
                      </button>
                      <button className="text-xs py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1.5 transition-colors hover:opacity-80"
                        style={{ background: 'rgba(100,116,139,0.15)', color: '#64748B' }}>
                        <XCircle size={12} /> Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
